/**
 * reCAPTCHA v3 Integration Utility
 * 
 * Features:
 * - Invisible CAPTCHA (no checkbox)
 * - Score-based validation (0.0 to 1.0)
 * - Easy integration across forms
 * - Backend validation ready
 * 
 * Usage:
 * import { executeCaptcha, loadRecaptchaScript } from './utils/captcha';
 * 
 * const token = await executeCaptcha('login');
 * // Send token to backend for verification
 */

// reCAPTCHA v3 Site Key (Frontend)
// This is a public key, safe to expose
export const RECAPTCHA_SITE_KEY = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Google's TEST key

// Load reCAPTCHA script
export const loadRecaptchaScript = () => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.grecaptcha && window.grecaptcha.execute) {
      resolve(window.grecaptcha);
      return;
    }

    // Check if script is already in DOM
    const existingScript = document.querySelector(`script[src*="recaptcha"]`);
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        // Wait for grecaptcha to be ready
        const checkReady = setInterval(() => {
          if (window.grecaptcha && window.grecaptcha.execute) {
            clearInterval(checkReady);
            resolve(window.grecaptcha);
          }
        }, 100);
      });
      return;
    }

    // Load script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      // Wait for grecaptcha to be ready
      const checkReady = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          clearInterval(checkReady);
          window.grecaptcha.ready(() => {
            resolve(window.grecaptcha);
          });
        }
      }, 100);
    };
    
    script.onerror = () => {
      reject(new Error('Failed to load reCAPTCHA script'));
    };
    
    document.head.appendChild(script);
  });
};

/**
 * Execute reCAPTCHA and get token
 * @param {string} action - Action name (e.g., 'login', 'signup', 'submit')
 * @returns {Promise<string>} - reCAPTCHA token
 */
export const executeCaptcha = async (action = 'submit') => {
  try {
    const grecaptcha = await loadRecaptchaScript();
    
    return new Promise((resolve, reject) => {
      grecaptcha.ready(async () => {
        try {
          const token = await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
          resolve(token);
        } catch (error) {
          console.error('reCAPTCHA execution error:', error);
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error('reCAPTCHA loading error:', error);
    throw error;
  }
};

/**
 * Validate captcha token on backend
 * This is just a helper, actual validation happens server-side
 */
export const verifyCaptchaToken = async (token, action) => {
  // This will be implemented in backend
  // Frontend just passes the token
  return { token, action };
};

export default {
  loadRecaptchaScript,
  executeCaptcha,
  RECAPTCHA_SITE_KEY
};
